import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { LabelerData, LabelingStatus, QCData } from "../api/labelApiSlice";
import { ConvoLabelers } from "../../components/LabelerSelect";
import {
  CompletionStatus,
  LabelTaskKey,
  LastUpdatedTasksMap,
  MessageStatus,
  QCCorrectLabelsMap,
  QCMessageCorrection,
  TaskStatusesMap,
  isMessageTask,
  isTranslatedMessageTask,
} from "../../utils/taskUtils";
import { LabelKeys, LabelTask } from "../label/labelTypes";

interface LabelState {
  labeler: LabelerData | undefined; // user for labeler role or selected labeler for qc role
  qc: QCData | undefined;
  convoLabelers: ConvoLabelers | undefined; // selecting a labeler for qc role
  taskStatuses: TaskStatusesMap<LabelKeys> | undefined; // completion status of tasks
  qcCorrectLabels: QCCorrectLabelsMap<LabelKeys> | undefined; // qc determines if a label is correct
  lastUpdatedTasks: LastUpdatedTasksMap;
  schemaVersion: string | undefined;
}

const initialState = {
  labeler: undefined,
  qc: undefined,
  convoLabelers: undefined,
  taskStatuses: undefined,
  qcCorrectLabels: undefined,
  lastUpdatedTasks: {},
  schemaVersion: undefined
} satisfies LabelState as LabelState;

const labelSlice = createSlice({
  name: "label",
  initialState,
  reducers: {
    setLabeler: (
      state: LabelState,
      action: PayloadAction<LabelerData | undefined>
    ) => {
      state.labeler = action.payload;
    },
    setQC: (state: LabelState, action: PayloadAction<QCData | undefined>) => {
      state.qc = action.payload;
    },
    setConvoLabelers: (
      state: LabelState,
      action: PayloadAction<ConvoLabelers>
    ) => {
      state.convoLabelers = action.payload;
    },
    setTaskStatuses: (
      state: LabelState,
      action: PayloadAction<TaskStatusesMap<LabelKeys> | undefined>
    ) => {
      state.taskStatuses = action.payload;
    },
    setQCCorrectLabels: (
      state: LabelState,
      action: PayloadAction<QCCorrectLabelsMap<LabelKeys> | undefined>
    ) => {
      state.qcCorrectLabels = action.payload;
    },
    setLastUpdatedTasks: (
      state: LabelState,
      action: PayloadAction<LastUpdatedTasksMap>
    ) => {
      state.lastUpdatedTasks = action.payload;
    },
    setSchemaVersion:  (
      state: LabelState,
      action: PayloadAction<string | undefined>
    ) => {
      state.schemaVersion = action.payload;
    },
    updateLabelerStatus: (
      state: LabelState,
      action: PayloadAction<{ labeler_status: LabelingStatus }>
    ) => {
      if (state.labeler) {
        state.labeler.labeler_status = action.payload.labeler_status;
      }
    },
    updateQCStatus: (
      state: LabelState,
      action: PayloadAction<{ qc_status: LabelingStatus }>
    ) => {
      if (state.qc) {
        state.qc.qc_status = action.payload.qc_status;
      }
    },
    updateConvoLabelers: (
      state: LabelState,
      action: PayloadAction<{
        convoUID: string;
        labeler: LabelerData | undefined;
      }>
    ) => {
      const { convoUID, labeler } = action.payload;
      if (state.convoLabelers?.[convoUID] && labeler) {
        state.convoLabelers[convoUID].selectedLabeler = labeler;
        state.labeler = labeler;
      }
    },
    updateTaskStatuses: (
      state: LabelState,
      action: PayloadAction<{
        taskID: string;
        newTaskStatus: CompletionStatus;
        messageUID?: string;
      }>
    ) => {
      if (state.taskStatuses) {
        const { taskID, newTaskStatus, messageUID } = action.payload;
        const currentTaskID = taskID as keyof typeof state.taskStatuses;
        const current = state.taskStatuses[currentTaskID];

        if (typeof current === "object") {
          if (!messageUID) {
            console.warn("Missing messageUID for message status update");
            return;
          }
          const msgStatus: MessageStatus = {
            ...current,
            [messageUID]: newTaskStatus,
          };

          state.taskStatuses[currentTaskID] = msgStatus as MessageStatus &
            CompletionStatus;
        } else {
          state.taskStatuses[currentTaskID] = newTaskStatus as MessageStatus &
            CompletionStatus;
        }
        if (messageUID) {
          state.lastUpdatedTasks[taskID as LabelTaskKey] = messageUID;
        }
      }
    },
    updateQCCorrectLabels: (
      state: LabelState,
      action: PayloadAction<{
        task: LabelTask;
        correct: boolean | null;
        messageUID?: string;
      }>
    ) => {
      const { task, messageUID, correct } = action.payload;
      state.qcCorrectLabels ??= {} as QCCorrectLabelsMap<LabelKeys>;
      const currentTaskID = task.id as keyof typeof state.qcCorrectLabels;

      if (task.id in state.qcCorrectLabels) {
        const current = state.qcCorrectLabels[currentTaskID];
        if (current && typeof current === "object" && messageUID) {
          state.qcCorrectLabels[currentTaskID] = {
            ...current,
            [messageUID]: correct,
          } as QCMessageCorrection & boolean;
        } else {
          state.qcCorrectLabels[currentTaskID] =
            correct as QCMessageCorrection & boolean;
        }
      } else {
        if (
          (isMessageTask(task.content) ||
            isTranslatedMessageTask(task.content)) &&
          messageUID
        ) {
          state.qcCorrectLabels[currentTaskID] = {
            [messageUID]: true,
          } as QCMessageCorrection & boolean;
        } else {
          state.qcCorrectLabels[currentTaskID] = true as QCMessageCorrection &
            boolean;
        }
      }
      if (messageUID) {
        state.lastUpdatedTasks[task.id as LabelTaskKey] = messageUID;
      }
    },
    updateLastUpdatedTasks: (
      state: LabelState,
      action: PayloadAction<{
        taskId: string;
        metric?: string;
      }>
    ) => {
      const { taskId, metric } = action.payload;
      state.lastUpdatedTasks[taskId as LabelTaskKey] = metric;
    },
    clearLabel: (state: LabelState) => {
      state.labeler = undefined;
      state.qc = undefined;
      state.convoLabelers = undefined;
      state.taskStatuses = undefined;
      state.qcCorrectLabels = undefined;
      state.lastUpdatedTasks = {};
      state.schemaVersion = undefined;
    },
  },
});

export const {
  setLabeler,
  setQC,
  setConvoLabelers,
  setTaskStatuses,
  setQCCorrectLabels,
  setLastUpdatedTasks,
  setSchemaVersion,
  updateLabelerStatus,
  updateQCStatus,
  updateConvoLabelers,
  updateTaskStatuses,
  updateQCCorrectLabels,
  updateLastUpdatedTasks,
  clearLabel,
} = labelSlice.actions;
export default labelSlice.reducer;
